import { memo } from 'react';
import { SpaceBetween, StatusIndicator } from '@amzn/awsui-components-core';
import { useContentViewPageViewModel } from './ContentViewPageViewModel';
import ContentLoadingErrorPage from '../v2-common/ContentLoadingErrorPage';
import FixedWidthContentContainer from '../v2-common/FixedWidthContentContainer';
import NotFound from '../common/NotFound';
import { LoadedContentView } from './LoadedContentView';
import { useLegacyWorkflowRedirect } from '../v2-hooks/useLegacyWorkflowRedirect';
import styles from './ContentViewPage.module.scss';

/**
 * Full page that renders an APGL content item for read-only viewing. Shows a
 * loading indicator, a not-found page (for missing or unauthorized content),
 * an error alert, or the loaded content body (title header plus the tabbed
 * viewing surface) depending on the view model status.
 */
const ContentViewPage = memo(function ContentViewPage() {
  const vm = useContentViewPageViewModel();

  const isRedirectingToLegacyPage = useLegacyWorkflowRedirect();

  // A missing or forbidden content renders the 404 page rather than exposing
  // the app chrome around an empty body.
  if (vm.status === 'notFound') return <NotFound />;

  // Peer-review stages are deprecated — redirect to v1 which still handles them.
  if (isRedirectingToLegacyPage) return null;

  return (
    <FixedWidthContentContainer>
      <div className={styles.container}>
        <SpaceBetween size="l">
          {vm.status === 'loading' && (
            <StatusIndicator type="loading">Loading content</StatusIndicator>
          )}
          {vm.status === 'error' && <ContentLoadingErrorPage message={vm.errorMessage} />}
          {vm.status === 'loaded' && <LoadedContentView isPageLoadTracked />}
        </SpaceBetween>
      </div>
    </FixedWidthContentContainer>
  );
});

export { ContentViewPage };
